import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { Alert } from '../../components/ui/index.js';

/**
 * Equipment detail codes, named — same pattern as `modules/finance/finance-errors.tsx`.
 *
 * The equipment routes answer with a stable top-level `error.code` (already
 * covered by the generic `errors.*` catalogue) PLUS a `details[].code` naming
 * exactly which business rule refused the request (contract section 5,
 * logs/eq-api-summary.txt). `ErrorNotice` alone would show a generic message
 * for every one of these; this module shows the API's ACTUAL result
 * (CLAUDE.md section 8), in both languages, under `equipment.errors.*`.
 */
const DETAIL_CODE_KEYS: Readonly<Record<string, string>> = {
  equipment_retired: 'equipmentRetired',
  calibrated_on_in_future: 'calibratedOnInFuture',
  responsible_user_not_member: 'responsibleUserNotMember',
  certificate_removed: 'certificateRemoved',
  already_registered: 'alreadyRegistered',
};

/** The first detail code this catalogue knows how to name, or null. */
function namedDetailCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  for (const detail of error.details) {
    if (detail.code && DETAIL_CODE_KEYS[detail.code]) return detail.code;
  }
  return null;
}

/**
 * The refusal, rendered as what actually happened.
 *
 * Shows the SPECIFIC sentence for a named equipment detail code; falls back
 * to the generic `errors.<code>` message for anything else — tenant
 * isolation's `404`, a missing permission's `403`, a network failure.
 * Renders nothing for no error, exactly like `ErrorNotice`.
 */
export function EquipmentMutationError({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;

  const code = namedDetailCode(error);
  if (code) {
    return <Alert tone="warning" title={t(`equipment.errors.${DETAIL_CODE_KEYS[code]}`)} />;
  }
  return <ErrorNotice error={error} />;
}
